import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Popconfirm, Space, Switch, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { useActiveLanguages } from '@features/languages/useLanguages';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { popupsApi } from '@api/popups';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { PopupFormModal } from '../components/PopupFormModal';
import { DateWindow } from './BannersPage';
import type { Popup, PopupInput, PopupListItem } from '../types';

const { Text } = Typography;

export function PopupsPage() {
  const { t } = useTranslation(['widgets', 'common']);
  const td = t as unknown as (key: string) => string;
  const qc = useQueryClient();
  const canManage = useCan(PERMISSIONS.POPUP_MANAGE);

  const [languageId, setLanguageId] = useState<string | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Popup | null>(null);
  const [loadingEditId, setLoadingEditId] = useState<string | null>(null);

  const { data: languages = [] } = useActiveLanguages();
  const langCodeById = useMemo(() => new Map(languages.map((l) => [l.id, l.code])), [languages]);

  const { data: popups = [], isLoading } = useQuery({
    queryKey: ['popups', { languageId }],
    queryFn: () => popupsApi.list({ languageId }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['popups'] });

  const createMutation = useMutation({
    mutationFn: (input: PopupInput) => popupsApi.create(input),
    onSuccess: async () => {
      toast.success(t('popup.toast.created'));
      setModalOpen(false);
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; input: PopupInput }) => popupsApi.update(vars.id, vars.input),
    onSuccess: async () => {
      toast.success(t('popup.toast.updated'));
      setModalOpen(false);
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const publishMutation = useMutation({
    mutationFn: (row: PopupListItem) => popupsApi.setPublished(row.id, row.status !== 'Published'),
    onSuccess: () => invalidate(),
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => popupsApi.remove(id),
    onSuccess: async () => {
      toast.success(t('popup.toast.deleted'));
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (row: PopupListItem) => {
    setLoadingEditId(row.id);
    void popupsApi
      .get(row.id)
      .then((full) => {
        setEditing(full);
        setModalOpen(true);
      })
      .catch((err) => toast.error(resolveApiErrorMessage(err)))
      .finally(() => setLoadingEditId(null));
  };

  const columns: ColumnsType<PopupListItem> = [
    { title: t('fields.name'), dataIndex: 'name', key: 'name' },
    {
      title: t('fields.frequency'),
      dataIndex: 'frequency',
      key: 'frequency',
      render: (f: string) => <Tag>{f ? td(`frequency.${f}`) : '—'}</Tag>,
    },
    {
      title: t('fields.language'),
      key: 'language',
      render: (_, r) =>
        r.languageId ? <Tag color="blue">{langCodeById.get(r.languageId) ?? r.languageId.slice(0, 8)}</Tag> : null,
    },
    {
      title: t('fields.dateRange'),
      key: 'dates',
      render: (_, r) => <DateWindow start={r.startDate} end={r.endDate} dash={t('common:labels.none')} />,
    },
    {
      title: t('fields.status'),
      key: 'status',
      render: (_, r) => (
        <Space size={8}>
          <Switch
            size="small"
            checked={r.status === 'Published'}
            disabled={!canManage || publishMutation.isPending}
            onChange={() => publishMutation.mutate(r)}
            aria-label={t('fields.status')}
          />
          <Tag color={r.status === 'Published' ? 'green' : 'default'}>
            {r.status === 'Published' ? t('status.published') : t('status.draft')}
          </Tag>
        </Space>
      ),
    },
    {
      title: t('fields.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (v: string) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—'),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      align: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<Pencil size={15} />}
            onClick={() => openEdit(r)}
            disabled={!canManage}
            loading={loadingEditId === r.id}
            aria-label={t('common:actions.edit')}
          />
          <Popconfirm
            title={t('popup.delete.title')}
            description={t('popup.delete.confirm', { name: r.name })}
            okText={t('common:actions.delete')}
            okButtonProps={{ danger: true }}
            cancelText={t('common:actions.cancel')}
            onConfirm={() => deleteMutation.mutate(r.id)}
            disabled={!canManage}
          >
            <Button type="text" size="small" danger icon={<Trash2 size={15} />} disabled={!canManage} aria-label={t('common:actions.delete')} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('popup.title')}
        description={t('popup.description')}
        breadcrumb={[{ label: t('common:labels.home'), to: '/' }, { label: t('popup.title') }]}
        actions={
          canManage ? (
            <Button type="primary" icon={<Plus size={16} />} onClick={openCreate}>
              {t('popup.new')}
            </Button>
          ) : null
        }
      />
      <Card>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <LanguagePicker
              value={languageId}
              onChange={setLanguageId}
              allowClear
              placeholder={t('common:contentLanguage.filterAll')}
              style={{ minWidth: 180 }}
            />
          </div>
          <Table<PopupListItem>
            rowKey="id"
            columns={columns}
            dataSource={popups}
            loading={isLoading}
            pagination={false}
            locale={{ emptyText: <Text type="secondary">{t('popup.empty')}</Text> }}
          />
        </Space>
      </Card>
      <PopupFormModal
        open={modalOpen}
        editing={editing}
        saving={createMutation.isPending || updateMutation.isPending}
        onCancel={() => setModalOpen(false)}
        onCreate={(input) => createMutation.mutate(input)}
        onUpdate={(id, input) => updateMutation.mutate({ id, input })}
      />
    </div>
  );
}
